import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { isLeadCarryingRole } from 'cpool-utils';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { SchemaCode, SchemaUser } from '../schemas/login';
import { schemaCode, schemaUser } from '../schemas/login';
import type { LoginState } from '../types/login';
import { APICommunicator } from '~/api/APICommunicator';
import { ReactComponent as Logo } from '~/assets/CPOOL-Logo.svg';
import TreatButton from '~/components/TreatButton';
import { useUserStore } from '~/store/userStore';

const clsInput =
    'bg-white form-input border-transparent w-full placeholder:text-dark text-grey text-xs px-3 py-2.5 mb-4';

export default function LoginRoute() {
    const queryClient = useQueryClient();
    const { useNavigate, useSearch } = getRouteApi('/_auth/login/');
    const { t } = useTranslation();
    const [state, setState] = useState<LoginState>({ loggedIn: false });
    const methodsCheckUser = useForm<SchemaUser>({
        resolver: zodResolver(schemaUser),
    });
    const methodsCode = useForm<SchemaCode>({
        resolver: zodResolver(schemaCode),
    });

    const navigate = useNavigate();
    const { redirect } = useSearch();

    useEffect(() => {
        queryClient.invalidateQueries();
        (async () => {
            if (await APICommunicator.renew()) navigate({ to: redirect || '/' });
        })();
    }, [navigate, queryClient, redirect]);

    const onSubmit: SubmitHandler<SchemaUser> = async (data) => {
        try {
            const { success } = await APICommunicator.checkUser(data.email, data.password);
            if (!success) {
                toast.error(t('LOGIN.INVALID_USER_LOGIN'), { theme: 'colored' });
                setState({ loggedIn: false });
            } else {
                setState({ loggedIn: true, ...data });
            }
        } catch (error) {
            toast.error(t('LOGIN.ERROR'), { theme: 'colored' });
            setState({ loggedIn: false });
            throw error;
        }
    };

    const onError: SubmitErrorHandler<SchemaUser> = (errors) => {
        if (errors.email) {
            toast.error(t('LOGIN.INVALID_EMAIL'), { theme: 'colored' });
        } else if (errors.password) {
            toast.error(t('LOGIN.INVALID_PASSWORD'), { theme: 'colored' });
        } else {
            toast.error(t('LOGIN.ERROR'), { theme: 'colored' });
        }
    };

    const onSubmitLogin: SubmitHandler<SchemaCode> = async (data) => {
        if (!state.loggedIn) {
            throw new Error('How did you get here?');
        }
        try {
            const { success } = await APICommunicator.login(state.email, state.password, data.code);
            if (!success) {
                toast.error(t('LOGIN.INVALID_2FA'), { theme: 'colored' });
            } else {
                const { user } = useUserStore.getState();
                if (!user) return;
                if (isLeadCarryingRole(user.role)) navigate({ to: redirect || '/dashboard' });
                else navigate({ to: redirect || '/' });
            }
        } catch (error) {
            toast.error(t('LOGIN.ERROR'), { theme: 'colored' });
            throw error;
        }
    };

    const onErrorLogin: SubmitErrorHandler<SchemaCode> = (errors) => {
        if (errors.code) {
            toast.error(t('LOGIN.2FA_CODE_NOT_6_CARACHTERS'), { theme: 'colored' });
        }
    };

    const onCodeRequest = () => {
        // TODO send a request to get a new 2FA Code
    };

    return (
        <div className="flex items-center justify-center w-full min-h-screen p-4">
            <div className="w-full max-w-[420px] p-4 mx-auto bg-grey-light">
                <h3 className="mb-4 text-3xl font-bold text-center text-blue">
                    <Logo />
                </h3>

                {!state.loggedIn ? (
                    <form key="checkUser" onSubmit={methodsCheckUser.handleSubmit(onSubmit, onError)}>
                        <input
                            {...methodsCheckUser.register('email')}
                            type="text"
                            className={clsInput}
                            placeholder={t('LOGIN.PLACEHOLDER_EMAIL')}
                            aria-label={t('LOGIN.EMAIL_LABEL')}
                            autoComplete="email"
                        />
                        <input
                            {...methodsCheckUser.register('password')}
                            type="password"
                            className={clsInput}
                            placeholder={t('LOGIN.PASSWORD_LABEL')}
                            aria-label={t('LOGIN.PASSWORD_LABEL')}
                            autoComplete="current-password"
                        />
                        <div className="flex items-center justify-end space-x-5">
                            <Link to="/request/password" className="inline-block text-xs text-grey">
                                {t('LOGIN.RECOVER_PASSWORD')}
                            </Link>
                            <TreatButton className="text-xs font-semibold bg-blue py-2 px-3.5 text-white">
                                {t('LOGIN.ENTER')}
                            </TreatButton>
                        </div>
                    </form>
                ) : (
                    <form key="login" onSubmit={methodsCode.handleSubmit(onSubmitLogin, onErrorLogin)}>
                        <input
                            {...methodsCode.register('code')}
                            type="text"
                            className={clsInput}
                            placeholder={t('LOGIN.PLACEHOLDER_2FA')}
                            aria-label={t('LOGIN.2FA_CODE_LABEL')}
                            autoComplete="one-time-code"
                        />
                        <div className="flex items-center justify-end space-x-5">
                            <div onClick={onCodeRequest} className="inline-block text-xs text-grey cursor-pointer">
                                {t('LOGIN.RECOVER_2FA')}
                            </div>
                            <TreatButton className="text-xs font-semibold bg-blue py-2 px-3.5 text-white">
                                {t('LOGIN.ENTER')}
                            </TreatButton>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
